/** @jsxImportSource @emotion/react */
import { ReactNode } from 'react';
import { css } from '@emotion/react';
import { Button } from '@mui/material';
import { ControlBtnGroup } from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import { ButtonDesign } from 'components/buttons/ButtonStyle';

/**
 * 접기 버튼 — 아이콘만 있는 작은 셰브론.
 *
 * 제목줄 맨 끝에 홀로 놓이다 보니 사각 테두리가 있으면 제목과 따로 노는 느낌이라,
 * 테두리·배경을 빼고 아이콘만 남긴다(hover 때 제목 색으로 짙어지는 정도).
 *
 * ControlBtnGroup이 자식 버튼에 `button { …IconLfSM …outlineNormal }`을 자손 셀렉터로
 * 걸어두는데 그쪽 특이도가 더 높아 css prop만으로는 덮이지 않는다. 그래서 !important를 쓴다.
 *
 * 셰브론은 아래를 향하므로(= 펼치기) 펼쳐져 있을 때만 180도 돌린다.
 */
const foldButtonStyle = css`
  padding: 0 !important;
  border: none !important;
  background: transparent !important;
  &:hover,
  &:active {
    border: none !important;
    background: transparent !important;
  }
  &:before {
    left: 50% !important;
    transform: translate(-50%, -50%) !important;
  }
  &:hover:before {
    background: var(--primary) !important;
  }
  &.open:before {
    transform: translate(-50%, -50%) rotate(180deg) !important;
  }
`;

interface Props {
  /**
   * 섹션 번호 (①, ②, …). 화면에서 작업 순서를 짚어주는 표식이라 제목 앞에 항상 붙인다.
   */
  no: string;
  title: string;
  /** 제목 옆 건수 — 숫자를 넘기면 "총 N건"으로 그린다 */
  count?: number;
  /** 건수 대신 넣을 보조 문구 (건수와 같이 쓸 수도 있다) */
  note?: ReactNode;
  folded: boolean;
  onToggleFold: () => void;
  /** 접기 버튼 왼쪽에 놓을 섹션별 액션 버튼 */
  children?: ReactNode;
}

/**
 * 기계 탭 각 섹션의 머리 — 이 화면 고유의 "파란 제목 + 가로선"(.section-title)을 유지하면서,
 * 우측에 액션 버튼과 접기 버튼을 얹는다.
 *
 * 가로선은 `.section-title::after { flex: 1 }`이라 order 0 무리의 맨 뒤에 놓인다.
 * 그래서 버튼 묶음만 order: 1로 보내면 "제목 → 선 → 버튼" 순서가 되고,
 * 마크업에서 선을 별도 요소로 꺼낼 필요가 없다 (styles.ts의 `.section-actions` 참고).
 */
const SectionHeader = ({ no, title, count, note, folded, onToggleFold, children }: Props) => (
  <div className="section-title">
    <span>
      {no} {title}
    </span>
    {typeof count === 'number' && (
      <span className="section-count">
        총 <strong>{count.toLocaleString()}</strong>건
      </span>
    )}
    {note && <span className="section-note">{note}</span>}
    <ControlBtnGroup className="section-actions">
      {children}
      <Button
        css={[IconButton.button, ButtonDesign.IconSM, foldButtonStyle]}
        className={`chevron${folded ? '' : ' open'}`}
        onClick={onToggleFold}
        title={folded ? '펼치기' : '접기'}
        disableRipple
      />
    </ControlBtnGroup>
  </div>
);

export default SectionHeader;
